import { useRemoteConfig } from 'core/hooks/use-remote-config';
import { request as r } from './network';
import { PaymentSystem } from '@web-solutions/react-billing';

let billingBaseUrlOverride = null;
let currentPaymentSystem = null;

export function setBillingBaseUrlOverride(url) {
  billingBaseUrlOverride = url || null;
}

export function setCurrentPaymentSystem(paymentSystem) {
  currentPaymentSystem = paymentSystem;
}

// For Stripe every request must hit the Voery API, picking stage vs. prod from
// the current URL. This is environment-derived (window.location + env), so it
// resolves correctly even before Billing.init() has run (e.g. the very first
// POST /stripe/customers on the email page).
export function getStripeBaseUrl() {
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const prodUrl = process.env.REACT_APP_VOERY_PROD_URL;
  const isProd = prodUrl && currentUrl.startsWith(prodUrl) && !currentUrl.includes('stage');

  return isProd
    ? process.env.REACT_APP_API_VOERY_BASE_URL_PROD
    : process.env.REACT_APP_API_VOERY_BASE_URL_STAGE;
}

// Single source of truth for the billing base URL: Stripe -> Voery API,
// everything else -> override (set in init) or the default billing/base URL.
function getBillingBaseUrl() {
  if (currentPaymentSystem === PaymentSystem.STRIPE) {
    return getStripeBaseUrl();
  }

  return (
    billingBaseUrlOverride ||
    process.env.REACT_APP_API_BILLING_URL ||
    process.env.REACT_APP_API_BASE_URL
  );
}

function request(headers = {}) {
  const baseUrl = getBillingBaseUrl();
  console.log('[API] request() called, baseUrl override:', billingBaseUrlOverride);
  console.log('[API] current payment system:', currentPaymentSystem);
  console.log('[API] final baseUrl:', baseUrl);

  // For Stripe, use /stripe endpoint. For others use /payments
  const endpoint = currentPaymentSystem === PaymentSystem.STRIPE ? '/stripe' : '/payments';
  console.log('[API] Using endpoint:', endpoint);

  return r(`${baseUrl}${endpoint}`, headers);
}

/**
 * @typedef {{
 *  data: Array,
 *  meta: {
 *    include: Array,
 *    custom: Array,
 *    pagination: {
 *      total: number,
 *      count: number,
 *      per_page: number,
 *      current_page: number,
 *      total_pages: number,
 *      links: Object
 *    }
 *  }
 * } Response
 */

/**
 * Basically payment_system_project and payment_system_type for all methods are enums. For Reflectio payment_system_project=default, payment_system_type=recurly
 */
const API = {
  /**
   *
   * @param {{ payment_system_project: string, payment_system_type: string }} params
   * @param {*} headers
   * @returns { Promise<Response> }
   */
  getPublicKey(params, headers) {
    console.log('[API] getPublicKey() called with params:', params);
    const isStripe = !params || Object.keys(params).length === 0;

    if (isStripe) {
      const baseUrl = getStripeBaseUrl();
      console.log('[API] Stripe getPublicKey - making GET /stripe/config request to:', baseUrl);
      return r(baseUrl, headers).get('/stripe/config')
        .then(response => {
          console.log('[API] getPublicKey response:', response);
          return response;
        })
        .catch(error => {
          console.error('[API] getPublicKey error:', error);
          throw error;
        });
    }

    const req = request(headers);
    console.log('[API] making GET /config request');
    return req.get('/config', { params })
      .then(response => {
        console.log('[API] getPublicKey response:', response);
        return response;
      })
      .catch(error => {
        console.error('[API] getPublicKey error:', error);
        throw error;
      });
  },

  /**
   *
   * @param {{
   *  search: string|undefined, // You can use search=external_id:1,2,3&searchFields=external_id:in to get specific products
   *  searchFields: string|undefined,
   *  payment_system_project:string,
   *  payment_system_type:string
   * }} params
   * @param {*} headers
   * @returns { Promise<Response> }
   */
  getPrices(params, headers) {
    return request(headers).get('/products', { params });
  },

  /**
   *
   * @param {{
   *  product_id:string,
   *  order_description:string,
   *  payment_system_project:string,
   *  payment_system_type:string
   * }} params
   * @param {*} headers
   * @returns { Promise<Response> }
   */
  createOrder(body, headers) {
    return request(headers).post('/orders', body);
  },

  initAlternativePayment(body, headers) {
    return request(headers).post('/orders/solid-alternative', body);
  },

  createCheckout(body, headers) {
    return request(headers).post('/checkouts', body);
  },

  /**
   *
   * @param {{
   *  email: string|undefined,
   *  payment_system_project: string,
   *  payment_system_type: string,
   *  domain: string|undefined,
   *  ip: string|undefined,
   *  user_agent: string|undefined,
   *  fbc: string|undefined,
   *  fbp: string|undefined
   * }} body
   * @param {*} headers
   * @returns { Promise<Response> }
   */
  createCustomer(body, headers) {
    return request(headers).post('/customers', body);
  },

  /**
   *
   * @param {{ search: string }} params*
   * @param {*} headers
   * @returns { Promise<Response> }
   */
  getActualizedSubscriptions(headers) {
    return request(headers).get('/subscriptions/actualized');
  },

  /**
   *
   * @param {{ search: string }} params*
   * @param {*} headers
   * @returns { Promise<Response> }
   */
  getAllSubscriptions(headers) {
    return request(headers).get('/subscriptions?all=true');
  },

  /**
   *
   * @param {{
   *  mail_link: string,
   *  price_id: string,
   *  token_id: string|undefined,
   *  payment_method_id: string|undefined,
   *  trial_period_days: string|undefined,
   *  invoice_price_id: string|undefined,
   *  payment_system_project: string,
   *  payment_system_type: string,
   *  domain: string|undefined,
   *  ip: string|undefined,
   *  user_agent: string|undefined,
   *  fbc: string|undefined,
   *  fbp: string|undefined
   * }} body
   * @param {*} headers
   * @returns { Promise<Response> }
   */
  createSubscription(body, headers) {
    return request(headers).post('/subscriptions', body);
  },

  /**
   *
   * @param {{
   *   payment_system_project: string,
   *   payment_system_type: string,
   *   product_id: string,
   *   success_url: string,
   *   fail_url: string,
   * }} body
   * @param {*} headers
   * @returns { Promise<Response> }
   */
  subscribeOnOneClick(body, headers) {
    return request(headers).post('/subscriptions/create-new-subscription-by-card-token', body);
  },

  createApplePayOrder(body, headers) {
    return request(headers).post('/apple-pay', body);
  },

  /**
   *
   * @param {{ email: string }} body
   * @param {*} headers
   * @returns { Promise<Response> }
   */
  cancelSubscription(body, headers) {
    return request(headers).post('/subscriptions/request-cancel', body);
  },

  /**
   *
   * @param {*} params
   * @param {{ token: string, email: string, sig: string }} headers
   * @returns { Promise<Response> }
   */
  submitCancelSubscription(params, headers) {
    return request(headers).get('/subscriptions/cancel', { params });
  },

  /**
   *
   * @param {{
   *  amount: number,
   *  description: string,
   *  currency: string,
   *  success_url: 'string',
   *  fail_url: 'string',
   *  product_code: 'string',
   * }} body
   * @param {*} headers
   * @returns { Promise<Response> }
   */
  createOneClickPayment(body, headers) {
    return request(headers).post('/one-time-payment', body);
  },

  getOneTimePurchases(headers) {
    return request(headers).get('/one-time-payment');
  },

  /**
   *
   * @param {{ search: string }} params*
   * @param {*} headers
   * @returns { Promise<Response> }
   */

  getPaymentMethod(headers) {
    return request(headers).get('/one-time-payment/get-payment-method');
  },

  /**
   *
   * @param {{
  *  subscriptionId: string,
  *  planCode: string,
  * }} body
  * @param {*} headers
  * @returns { Promise<Response> }
  */
  changePlan(body, headers) {
    return request(headers).post('/subscriptions/change-plan', body);
  },
  /**
     *
     * @param {{
  *  email: string,
  * }} body
  * @param {*} headers
  * @returns { Promise<Response> }
  */
  sendAccessEmail(body, headers) {
    return request(headers).post('/subscriptions/send-access-mail', body);
  },

  giveProduct(body, headers) {
    return request(headers).post('/one-time-payment/free', body);
  },

  getStripeProducts(type, headers) {
    const baseUrl = getStripeBaseUrl();
    console.log('[API] getStripeProducts() called with type:', type);
    console.log('[API] making GET /stripe/products request with type:', type);
    return r(baseUrl, headers).get('/stripe/products', { params: { type } })
      .then(response => {
        console.log('[API] getStripeProducts response:', response);
        return response;
      })
      .catch(error => {
        console.error('[API] getStripeProducts error:', error);
        throw error;
      });
  },

  createStripeCustomer(body, headers) {
    const baseUrl = getStripeBaseUrl();
    // Solo enviar {email}, sin payment_system_type ni payment_system_project.
    // collectEmailAfterPayment flow creates an anonymous customer with no email,
    // so send an empty body in that case.
    const cleanBody = body?.email ? { email: body.email } : {};
    console.log('[API] createStripeCustomer() called with cleanBody:', cleanBody);
    console.log('[API] baseUrl:', baseUrl);
    console.log('[API] billingBaseUrlOverride:', billingBaseUrlOverride);
    console.log('[API] making POST to:', baseUrl + '/stripe/customers');
    return r(baseUrl, headers).post('/stripe/customers', cleanBody)
      .then(response => {
        console.log('[API] createStripeCustomer response:', response);
        return response;
      })
      .catch(error => {
        console.error('[API] createStripeCustomer error:', error);
        throw error;
      });
  },

  createStripeOneTimePayment(body, headers) {
    console.log('[API] createStripeOneTimePayment() called with body:', body);
    return request(headers).post('/one-time-payment', body)
      .then(response => {
        console.log('[API] createStripeOneTimePayment response:', response);
        return response;
      })
      .catch(error => {
        console.error('[API] createStripeOneTimePayment error:', error);
        throw error;
      });
  },
};

export default API;
